import { Link } from "react-router-dom";
import { ArrowRight, MapPin, HardHat } from "lucide-react";
import { BannerHero, Container } from "@/components/site/Layout";
import { SectionHead, Reveal, ClaimBadge } from "@/components/site/Primitives";
import { JobBoard } from "@/components/site/JobBoard";
import { JobAlerts } from "@/components/site/JobAlerts";
import { useSeo } from "@/lib/useSeo";

const WHY = [
  { t: "Early-stage project", d: "Work alongside a leadership team of refining, trading and project veterans on a new-build refinery at the Port of Brownsville." },
  { t: "Based in Brownsville", d: "Roles are tied to the Port of Brownsville site. [Relocation support policy — HR to confirm.]" },
  { t: "Clear postings", d: "Each role lists department, location, a named owner and a closing date. [Compensation ranges — HR to supply.]" },
];

const STEPS = [
  ["Apply", "Submit the short form on the role page. You'll get a reference number."],
  ["Review", "The posting owner reviews within [X] business days."],
  ["Interviews", "[Number] conversations with the hiring team, on site or remote."],
  ["Offer", "Written offer with start date tied to the project schedule."],
];

export default function Careers() {
  useSeo({
    title: "Careers at America First Refining | Brownsville, TX Jobs",
    description: "Open roles at America First Refining in Brownsville, Texas. Search by department, see the hiring process and apply online.",
    path: "/careers",
  });
  return (
    <div data-testid="careers-page">
      <BannerHero testId="careers-banner" title="Careers" eyebrow="Brownsville, Texas" image="/img/afr-ship-channel.jpg" alt="Aerial photograph of the Port of Brownsville ship channel" caption="Port of Brownsville · Photo via AFR"
        intro="Build the refinery with us. Engineering, construction, environmental and commercial roles for a refinery under construction at the Port of Brownsville. Openings below are live from the jobs database." />
      <div className="section-light"><div data-testid="stage-note" className="mx-auto max-w-[1400px] px-5 pt-14 md:px-10 md:pt-20">
        <div className="hairline flex flex-col gap-6 bg-[#0B1D4F] p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div className="flex items-start gap-4">
            <HardHat className="mt-1 h-6 w-6 shrink-0 text-[#D4A24C]" />
            <p className="text-slate-200"><span className="font-semibold">Where the roles are:</span> the AFR site at the Port of Brownsville. Hiring follows the construction schedule, so roles open in waves.</p>
          </div>
          <span className="flex shrink-0 items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-slate-300"><MapPin className="h-4 w-4 text-[#D4A24C]" />Port of Brownsville, TX</span>
        </div>
      </div></div>
      <Container testId="why-section">
        <SectionHead eyebrow="01 · Why AFR" title="Why work here" />
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
          {WHY.map((w, i) => (
            <Reveal key={w.t} delay={i * 0.06} className="bg-[#0B1D4F] p-8">
              <h3 className="font-display text-3xl font-extrabold uppercase">{w.t}</h3>
              <p className="mt-3 text-slate-300">{w.d}</p>
            </Reveal>
          ))}
        </div>
      </Container>
      <Container id="openings" testId="openings-section" tone="soft">
        <SectionHead eyebrow="02 · Current openings" title="Open roles" body="Sample postings shown until AFR's real openings are posted." />
        <JobBoard />
        <div className="mt-14"><JobAlerts /></div>
      </Container>
      <Container testId="process-section" tone="light">
        <SectionHead eyebrow="03 · Hiring process" title="Four steps" />
        <ol className="grid gap-6 md:grid-cols-4">
          {STEPS.map(([t, d], i) => (
            <Reveal as="li" key={t} delay={i * 0.08} className="border-t-2 border-[#C8102E] pt-6">
              <p className="font-mono text-xs text-slate-300">STEP 0{i + 1}</p>
              <h3 className="font-display mt-2 text-3xl font-extrabold uppercase">{t}</h3>
              <p className="mt-2 text-sm text-slate-300">{d}</p>
            </Reveal>
          ))}
        </ol>
      </Container>
      <Container testId="general-apply-section">
        <div className="chamfer flex flex-col gap-6 bg-[#C8102E] p-8 md:flex-row md:items-center md:justify-between md:p-12">
          <div>
            <ClaimBadge type="fact" testId="general-apply-badge" />
            <h2 className="font-display mt-4 text-4xl font-black uppercase leading-none text-white md:text-5xl">Don't see your role?</h2>
            <p className="mt-3 max-w-xl text-white/90">Send a general inquiry to Talent Acquisition. We'll keep it on file for future openings.</p>
          </div>
          <Link to="/contact?audience=careers" data-testid="general-apply-cta" className="group inline-flex shrink-0 items-center gap-2 bg-white px-6 py-4 font-semibold text-[#001870]">Send your resume <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
        </div>
      </Container>
    </div>
  );
}
